import React, { useState } from 'react';
import snakeCaseKeys from 'snakecase-keys';
import { Col, Modal, Row } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { DatatableEmployeeName, DefaultModal, Reminder } from '../../../components';
import ApproveRejectStyledActions from '../../../components/DatatableActions/ApproveRejectStyledActions';
import ApprovementStatusStyled from '../../../components/DatatableColumns/ApprovementStatusStyled';
import WorkflowRequestReject from '../../WorkflowRequest/WorkflowRequestReject';
import SignatureKeyForm from '../../DocumentPreview/SignatureKeyForm';
import {
  approveOvertimeCompensationRequest,
  deleteOvertimeCompensationRequest,
  rejectOvertimeCompensationRequest
} from '../../../requests/overtimeCompensations';
import { signWorkflowRequestRequest } from '../../../requests/workflowRequests';
import { sendAlert } from '../../../actions/utils';

const OvertimeCompensationInfoTable = ({ item, setModalShow, moreData, setMoreData }) => {
  const dispatch = useDispatch();

  const [modalItem, setDefaultModalItem] = useState({});
  const [defaultModalTitle, setDefaultModalTitle] = useState('');
  const [defaultModalBody, setDefaultModalBody] = useState('');
  const [defaultModalShow, setDefaultModalShow] = useState(false);
  const [defaultModalButtons, setDefaultModalButtons] = useState(true);
  const [defaultModalAction, setDefaultModalAction] = useState(() => null);
  const [onRequest, setOnRequest] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [reminderShow, setReminderShow] = useState(false);
  const [reminderItem, setReminderItem] = useState({});

  const {
    employee,
    createdAt,
    startDate,
    endDate,
    reincorporationDate,
    daysTaken,
    extraHoursTaken,
    status,
    translatedStatus
  } = item;

  const handleModalClose = () => {
    setModalShow(false);
    setDefaultModalShow(false);
    setDefaultModalTitle('');
    setDefaultModalBody('');
    setOnRequest(false);
  };

  const handleSuccessAction = message => {
    setMoreData(!moreData);
    dispatch(sendAlert({ kind: 'success', message }));
    handleModalClose();
  };

  const handleFailureRequest = error => {
    const { response } = error;
    setIsDisabled(false);
    setOnRequest(false);
    dispatch(sendAlert({ kind: 'error', message: response?.data?.message }));
  };

  const approveWorkflow = workflowId => {
    const signDocument = signatureKey => {
      signWorkflowRequestRequest(workflowId, {
        dispatch,
        params: snakeCaseKeys(signatureKey),
        successCallback: () => handleSuccessAction('Solicitud aprobada con éxito'),
        failureCallback: error => handleFailureRequest(error)
      });
    };

    setDefaultModalButtons(false);
    setDefaultModalTitle('Firmar Documento');
    setDefaultModalBody(<SignatureKeyForm formRequest={signDocument} onHide={handleModalClose} signatureKey="" />);
    setDefaultModalShow(true);
  };

  const handleApproveRequest = () => {
    setOnRequest(true);
    approveOvertimeCompensationRequest(item.id, {
      dispatch,
      params: { id: item.id },
      successCallback: () => handleSuccessAction('Solicitud aprobada con éxito'),
      failureCallback: () => setOnRequest(false)
    });
  };

  const handleRejectRequest = (overtimeCompensation, setSubmitting) => {
    const { id: overtimeCompensationId } = overtimeCompensation.workflowRequest;
    const sendParams = snakeCaseKeys(overtimeCompensation);
    setOnRequest(true);
    rejectOvertimeCompensationRequest(overtimeCompensationId, {
      dispatch,
      params: sendParams,
      formData: true,
      successCallback: () => handleSuccessAction('Solicitud rechazada con éxito'),
      callback: () => {
        setOnRequest(false);
        setSubmitting(false);
      }
    });
  };

  const handleDeleteRequest = () => {
    setOnRequest(true);
    deleteOvertimeCompensationRequest(item.id, {
      dispatch,
      successCallback: () => handleSuccessAction('Solicitud eliminada con éxito'),
      callback: () => setOnRequest(false)
    });
  };

  const handleConfirmModal = () => {
    setIsDisabled(false);
    setOnRequest(false);
    setDefaultModalItem(item);
    setDefaultModalTitle('Eliminar solicitud de Días por HH.EE');
    setDefaultModalBody('¿Estás seguro que deseas eliminar esta solicitud?');
    setDefaultModalButtons(true);
    setDefaultModalAction(() => handleDeleteRequest);
    setDefaultModalShow(true);
  };

  const handleApproveModal = () => {
    setIsDisabled(false);
    setOnRequest(false);
    setDefaultModalItem(item);
    setDefaultModalButtons(true);
    setDefaultModalTitle('Aprobar Solicitud');
    setDefaultModalBody('¿Estás seguro que deseas aprobar esta solicitud?');
    setDefaultModalAction(() => handleApproveRequest);
    setDefaultModalShow(true);
  };

  const handleRejectModal = () => {
    setDefaultModalTitle('Rechazar Solicitud');
    setDefaultModalBody(
      <WorkflowRequestReject
        record={item}
        workflowRequest={item}
        cancelAction={handleModalClose}
        formRequest={handleRejectRequest}
      />
    );
    setDefaultModalShow(true);
    setDefaultModalButtons(false);
  };

  const handleActions = action => {
    switch (action) {
      case 'approved':
        if (item.requestReviewerSignature) {
          if (item.workflowRequestId) {
            approveWorkflow(item.workflowRequestId);
          } else {
            handleApproveModal();
          }
        } else {
          handleApproveModal();
        }
        break;
      case 'rejected':
        handleRejectModal();
        break;
      case 'destroy':
        handleConfirmModal();
        break;
      case 'resend':
        setReminderItem(snakeCaseKeys(item));
        setReminderShow(true);
        break;
      default:
        console.log('Acción no encontrada:', action);
        break;
    }
  };

  return (
    <>
      <Modal.Body>
        <div className="info-box">
          <Row>
            <Col md={2}>
              <ApprovementStatusStyled margin="mt-1" status={status} />
            </Col>
            <Col md={8} className="title-pw">
              <b>Gestionar Solicitud de Días por HH.EE</b>
            </Col>
            <Col md={2} />
          </Row>
          <table className="simple-table mt-3">
            <thead>
              <tr>
                <th style={{ textAlign: 'center', paddingTop: '25px' }}>Solicita</th>
                <th style={{ textAlign: 'center', paddingTop: '25px' }}>Fecha Solicitud</th>
                <th style={{ textAlign: 'center', paddingTop: '25px' }}>Fecha Inicio</th>
                <th style={{ textAlign: 'center', paddingTop: '25px' }}>Fecha Término</th>
                <th style={{ textAlign: 'center', paddingTop: '25px' }}>Fecha Retorno</th>
                <th style={{ textAlign: 'center', paddingTop: '25px' }}>Total Días</th>
                <th style={{ textAlign: 'center', paddingTop: '25px' }}>Total Horas</th>
                <th style={{ textAlign: 'center', paddingTop: '25px' }}>Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ width: '200px' }}>
                  <div bis_skin_checked="1" style={{ display: 'flex', alignItems: 'center' }}>
                    <div aria-hidden="true" bis_skin_checked="1" className="employee-info" />
                    <span style={{ display: 'flex', justifyContent: 'center' }}>
                      <DatatableEmployeeName item={employee} fileName="fileInfo" name="shortName" fileUrl="fileUrl" />
                    </span>
                  </div>
                </td>
                <td align="center">{createdAt}</td>
                <td align="center">{startDate}</td>
                <td align="center">{endDate}</td>
                <td align="center">{reincorporationDate}</td>
                <td align="right">{daysTaken}</td>
                <td align="right">{extraHoursTaken}</td>
                <td align="left">{translatedStatus}</td>
              </tr>
            </tbody>
          </table>
          <br />
          <Row>
            <Col className="text-center">
              <ApproveRejectStyledActions
                resource="OvertimeCompensationRequest"
                item={item}
                clickHandler={handleActions}
                approveText={item.requestReviewerSignature ? 'Firmar / Aprobar' : 'Aprobar'}
              />
            </Col>
          </Row>
        </div>
      </Modal.Body>

      <DefaultModal
        body={defaultModalBody}
        disabled={onRequest || isDisabled}
        handleClose={handleModalClose}
        handleConfirm={() => defaultModalAction(modalItem)}
        show={defaultModalShow}
        title={defaultModalTitle}
        titleBtnClose="Cancelar"
        titleBtnSave="Confirmar"
        withClose={defaultModalButtons}
        withConfirm={defaultModalButtons}
      />

      <Reminder
        item={reminderItem}
        show={reminderShow}
        modalShow={setReminderShow}
        moreData={moreData}
        setOnRequest={setOnRequest}
        setMoreData={setMoreData}
      />
    </>
  );
};

export default OvertimeCompensationInfoTable;
